import { getAnalytics, logEvent } from "firebase/analytics";
import { firebaseApp } from "../store/LoggerContext";

// Events for the website's get-the-app card (`components/MyAccount/GetAppCard`):
// at the top of My Account (#1259) and on the episode page (#1184), both Free
// Feed Increment 3. Copied from youtubeAnalytics.ts: the event goes to
// Firebase Analytics, and a failure is logged and the page carries on. The
// card's job is "to see what people do a little first" (Ellaie, 3 Nov), so
// each tap carries the member's status, the device and which link was used.
// The event name says where the card was; the details are the same for both.

export type GetAppEvent = "my_account_get_app_click" | "episode_get_app_click";

export type GetAppParams = {
  /** The member's status, e.g. `trial-expired`. */
  member_status: string;
  /** Phones and tablets are `phone`; everything else is `computer`. */
  device_type: "phone" | "computer";
  /** The phone's OPEN IN THE APP button, or one of the computer's badges. */
  target: "open" | "app_store" | "google_play";
};

// The prefix on the local log line when analytics fails, per event.
const LOG_PREFIX: Record<GetAppEvent, string> = {
  my_account_get_app_click: "[MyAccount]",
  episode_get_app_click: "[Episode]",
};

export const logGetAppEvent = (
  event: GetAppEvent,
  params: GetAppParams,
): void => {
  try {
    logEvent(getAnalytics(firebaseApp), event, params);
  } catch (e) {
    // Analytics blocked or unavailable (ad blocker, private window): the page
    // must still work, so log locally and carry on.
    console.error(`${LOG_PREFIX[event]} analytics event ${event} failed`, e);
  }
};

// #1261: the website trial for someone on a computer with no smartphone or
// tablet (My Account and sign-up's last screen). Each tap is counted, so we
// can see how many people choose the website over the app (done-when 7).
// Same Firebase route and failure handling as the get-the-app card above.

export type WebTrialEvent = "web_trial_card_click" | "web_trial_confirm_click";

export type WebTrialParams = {
  /** The member's status, e.g. `standard-incomplete`. */
  member_status: string;
  /** Where the card was: My Account or sign-up's last screen. */
  surface: "my_account" | "sign_up";
  /** On the confirm step only: which button was pressed. */
  choice?: "start" | "back" | "add_mobile";
};

export const logWebTrialEvent = (
  event: WebTrialEvent,
  params: WebTrialParams,
): void => {
  try {
    logEvent(getAnalytics(firebaseApp), event, params);
  } catch (e) {
    // Analytics blocked or unavailable: the tap must still go through.
    console.error(`[WebTrial] analytics event ${event} failed`, e);
  }
};
